import { localAssetStorageMode, type LocalAssetRecord } from '../types/localAsset';

const databaseName = 'calendar-studio-assets';
const databaseVersion = 1;
const assetStoreName = 'assets';

export function getLocalAssetKey(projectId: string, photoId: string) {
  return `${projectId}:${photoId}`;
}

function canUseIndexedDb() {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function loadImageDimensionsFromBlob(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !blob.type.startsWith('image/')) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const image = new window.Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: 0, height: 0 });
    };
    image.src = objectUrl;
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request selhal.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transakce byla zrušena.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transakce selhala.'));
  });
}

export function openCalendarStudioAssetDb(): Promise<IDBDatabase> {
  if (!canUseIndexedDb()) {
    return Promise.reject(new Error('IndexedDB není v tomto prohlížeči dostupné.'));
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(databaseName, databaseVersion);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(assetStoreName)) {
        const store = db.createObjectStore(assetStoreName, { keyPath: 'key' });
        store.createIndex('projectId', 'projectId', { unique: false });
        store.createIndex('photoId', 'photoId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB databázi se nepodařilo otevřít.'));
  });
}

export async function saveLocalAsset(projectId: string, photoId: string, file: Blob): Promise<LocalAssetRecord> {
  const db = await openCalendarStudioAssetDb();
  try {
    const key = getLocalAssetKey(projectId, photoId);
    const dimensions = await loadImageDimensionsFromBlob(file);
    const namedFile = file instanceof File ? file : undefined;
    const record: LocalAssetRecord = {
      key,
      projectId,
      photoId,
      fileName: namedFile?.name ?? `${photoId}.image`,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      width: dimensions.width,
      height: dimensions.height,
      storedAt: new Date().toISOString(),
      blob: file,
    };

    const transaction = db.transaction(assetStoreName, 'readwrite');
    transaction.objectStore(assetStoreName).put(record);
    await transactionDone(transaction);
    return record;
  } finally {
    db.close();
  }
}

export async function getLocalAssetByKey(key: string): Promise<LocalAssetRecord | null> {
  const db = await openCalendarStudioAssetDb();
  try {
    const transaction = db.transaction(assetStoreName, 'readonly');
    const record = await requestToPromise<LocalAssetRecord | undefined>(transaction.objectStore(assetStoreName).get(key));
    return record ?? null;
  } finally {
    db.close();
  }
}

export async function getLocalAsset(projectId: string, photoId: string): Promise<LocalAssetRecord | null> {
  return getLocalAssetByKey(getLocalAssetKey(projectId, photoId));
}

export async function deleteLocalAsset(projectId: string, photoId: string): Promise<void> {
  const db = await openCalendarStudioAssetDb();
  try {
    const transaction = db.transaction(assetStoreName, 'readwrite');
    transaction.objectStore(assetStoreName).delete(getLocalAssetKey(projectId, photoId));
    await transactionDone(transaction);
  } finally {
    db.close();
  }
}

export async function listLocalAssets(projectId: string): Promise<LocalAssetRecord[]> {
  const db = await openCalendarStudioAssetDb();
  try {
    const transaction = db.transaction(assetStoreName, 'readonly');
    const index = transaction.objectStore(assetStoreName).index('projectId');
    return await requestToPromise<LocalAssetRecord[]>(index.getAll(projectId));
  } finally {
    db.close();
  }
}

export async function clearLocalAssets(projectId: string): Promise<void> {
  const db = await openCalendarStudioAssetDb();
  try {
    const transaction = db.transaction(assetStoreName, 'readwrite');
    const store = transaction.objectStore(assetStoreName);
    const index = store.index('projectId');
    const keys = await requestToPromise<IDBValidKey[]>(index.getAllKeys(projectId));
    keys.forEach((key) => store.delete(key));
    await transactionDone(transaction);
  } finally {
    db.close();
  }
}

export async function createObjectUrlFromStoredAsset(projectId: string, photoId: string, localAssetKey?: string): Promise<string | null> {
  const record = localAssetKey ? await getLocalAssetByKey(localAssetKey) : await getLocalAsset(projectId, photoId);
  return record ? URL.createObjectURL(record.blob) : null;
}

export { localAssetStorageMode };
